// MergePlanner: the merge-on-join preview shown before binding a non-empty
// local folder to an existing vault (plugin spec §Merge-on-join). It does a
// read-only pass over the remote meta + content docs and the local disk
// snapshot and reports what a join would do; execution runs through the
// normal bridge queue (startup reconcile), with the backup-zip step left to
// the adapter via an exposed hook.

import { Mode } from "@cairn/proto";
import type { VaultPort } from "./ports";
import type { DocStore } from "./docStore";
import { META_DOC_ID, META_SYNC_TIMEOUT_MS } from "./docStore";
import { MetaView, contentText } from "./meta";
import { hashBytes } from "./attachments";
import { isIgnored, isTextFile, pathKey } from "./conflictPolicy";

const encoder = new TextEncoder();
function hashText(text: string): string {
  return hashBytes(encoder.encode(text).buffer as ArrayBuffer);
}

/** The dry-run preview rendered in the join modal (plugin spec). */
export interface MergePreview {
  /** Local-only files that will be uploaded/created remotely. */
  addsLocalToRemote: string[];
  /** Remote-only files that will be downloaded/created locally. */
  addsRemoteToLocal: string[];
  /** Same path, differing content — char-merged, no conflict files. */
  merges: string[];
  attachmentUploads: string[];
  attachmentDownloads: string[];
  /** Best-effort total bytes to transfer (content lengths where known). */
  bytes: number;
}

export class MergePlanner {
  constructor(
    private readonly vault: VaultPort,
    private readonly docs: DocStore,
    /** Budget for the blocking meta-doc sync gating the preview (default 30s). */
    private readonly metaSyncTimeoutMs = META_SYNC_TIMEOUT_MS,
  ) {}

  /**
   * Compute the join preview. Subscribes the meta + content docs read-only
   * and waits for their sync so remote text is available to diff.
   */
  async plan(): Promise<MergePreview> {
    await this.docs.subscribe(META_DOC_ID, Mode.MODE_LIVE);
    await this.docs.waitSynced(META_DOC_ID, this.metaSyncTimeoutMs);
    const meta = new MetaView(this.docs.getDoc(META_DOC_ID)!);
    const active = meta.active();
    for (const e of active) {
      if (e.kind === "md") await this.docs.subscribe(e.fileId, Mode.MODE_BACKGROUND);
    }
    for (const e of active) {
      if (e.kind === "md") {
        try {
          await this.docs.waitSynced(e.fileId);
        } catch {
          /* ignore a slow doc for the preview */
        }
      }
    }

    const preview: MergePreview = {
      addsLocalToRemote: [],
      addsRemoteToLocal: [],
      merges: [],
      attachmentUploads: [],
      attachmentDownloads: [],
      bytes: 0,
    };

    // Index remote entries by path key.
    const remoteByKey = new Map<string, { path: string; kind: string; hash?: string; fileId: string }>();
    for (const e of active) remoteByKey.set(pathKey(e.path), e);

    // Local disk snapshot.
    const listed = (await this.vault.list()).filter((s) => !isIgnored(s.path));
    const localKeys = new Set<string>();
    for (const stat of listed) {
      const key = pathKey(stat.path);
      localKeys.add(key);
      const remote = remoteByKey.get(key);
      if (!remote) {
        // Local-only.
        if (isTextFile(stat.path)) preview.addsLocalToRemote.push(stat.path);
        else preview.attachmentUploads.push(stat.path);
        preview.bytes += stat.size;
        continue;
      }
      // Present on both — compare content.
      if (isTextFile(stat.path) && remote.kind === "md") {
        const diskText = await this.vault.readText(stat.path);
        const doc = this.docs.getDoc(remote.fileId);
        const crdtText = doc ? contentText(doc).toString() : "";
        if (hashText(diskText) !== hashText(crdtText)) {
          preview.merges.push(stat.path);
          preview.bytes += encoder.encode(diskText).length;
        }
      } else if (!isTextFile(stat.path) && remote.kind === "attachment") {
        const bytes = await this.vault.readBinary(stat.path);
        if (hashBytes(bytes) !== remote.hash) {
          // Differing attachment content at the same path: upload the local.
          preview.attachmentUploads.push(stat.path);
          preview.bytes += stat.size;
        }
      }
    }

    // Remote-only files → will be created locally.
    for (const e of active) {
      if (localKeys.has(pathKey(e.path))) continue;
      if (e.kind === "attachment") preview.attachmentDownloads.push(e.path);
      else {
        preview.addsRemoteToLocal.push(e.path);
        const doc = this.docs.getDoc(e.fileId);
        if (doc) preview.bytes += encoder.encode(contentText(doc).toString()).length;
      }
    }

    return preview;
  }
}
